import { useEffect, useId, useMemo, useRef, useState, type MouseEventHandler } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appPromotionLogos, type AppPromotionSettings } from '@/config/appPromotion';
import { fetchPublicAppPromotion } from '@/services/appPromotionService';
import { Dialog } from '@/components/ui/dialog';
import { AppDownloadDialog } from '@/components/AppDownloadDialog';
import './AppPromotionStrip.css';

const campaignPhrase = 'Do Puja ఉందిగా!';

function Message({ text }: { text: string }) {
  const parts = text.split(campaignPhrase);
  return <span className="app-promo-message" lang="te">{parts.map((part, index) => (
    <span key={index}>{part}{index < parts.length - 1 && <strong className="app-promo-campaign">{campaignPhrase}</strong>}</span>
  ))}</span>;
}

function AppIdentity({ app, onOpenDownloadPanel, dialogId, open }: {
  app: 'pandit' | 'doPuja';
  onOpenDownloadPanel: MouseEventHandler<HTMLButtonElement>;
  dialogId: string;
  open: boolean;
}) {
  const logo = appPromotionLogos[app];
  return <button
    type="button"
    className={`app-promo-identity app-promo-identity--${app}`}
    aria-label={`Get the ${logo.name} app`}
    aria-haspopup="dialog"
    aria-expanded={open}
    aria-controls={open ? dialogId : undefined}
    onClick={onOpenDownloadPanel}
  >
    <img src={logo.src} alt={`${logo.name} app logo`} width="38" height="38" />
    <span className="app-promo-identity-name">{logo.name}</span>
  </button>;
}

function ContentGroup({ settings }: { settings: AppPromotionSettings }) {
  const activeMessages = settings.messages.filter(message => message.enabled && message.text.trim());
  return <div className="app-promo-group">
    <Message text={settings.openingMessage} />
    {activeMessages.map(message => <span className="app-promo-entry" key={message.id}>
      <span className="app-promo-dot" aria-hidden="true" />
      <Message text={message.text} />
    </span>)}
    <span className="app-promo-dot" aria-hidden="true" />
  </div>;
}

export function AppPromotionStrip({ previewSettings }: {
  previewSettings?: AppPromotionSettings;
}) {
  const { data } = useQuery({
    queryKey: ['app-promotion'], queryFn: fetchPublicAppPromotion,
    enabled: !previewSettings, staleTime: 60_000, retry: 1,
  });
  const settings = previewSettings ?? data;
  const [downloadOpen, setDownloadOpen] = useState(false);
  const downloadTriggerRef = useRef<HTMLButtonElement | null>(null);
  const dialogId = useId();
  const onOpenDownloadPanel: MouseEventHandler<HTMLButtonElement> = event => {
    downloadTriggerRef.current = event.currentTarget;
    setDownloadOpen(true);
  };
  const viewportRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);
  const [groupWidth, setGroupWidth] = useState(0);
  const [touchPaused, setTouchPaused] = useState(false);
  const speed = Math.max(20, Math.min(150, settings?.speedPixelsPerSecond ?? 42));
  const duration = useMemo(() => groupWidth ? `${groupWidth / speed}s` : undefined, [groupWidth, speed]);

  useEffect(() => {
    const group = groupRef.current;
    const viewport = viewportRef.current;
    if (!group || !viewport) return;
    const measure = () => setGroupWidth(group.getBoundingClientRect().width);
    const observer = new ResizeObserver(measure);
    observer.observe(group);
    observer.observe(viewport);
    measure();
    document.fonts?.ready.then(measure);
    return () => observer.disconnect();
  }, [settings]);

  if (!settings) {
    return <div className="app-promo-placeholder" aria-hidden="true">
      <span>OUR APPS</span>
    </div>;
  }
  if (!settings.enabled) return null;

  return <Dialog open={downloadOpen} onOpenChange={setDownloadOpen}>
    <section className={`app-promo${touchPaused || downloadOpen ? ' app-promo--paused' : ''}`} aria-label="Our apps">
    <div className="app-promo-label">OUR APPS</div>
    <AppIdentity app="pandit" onOpenDownloadPanel={onOpenDownloadPanel} dialogId={dialogId} open={downloadOpen} />
    <div
      className="app-promo-viewport"
      ref={viewportRef}
      tabIndex={0}
      role="region"
      aria-label="App promotion messages. Focus to pause scrolling. On touch screens, tap to pause or resume."
      onPointerUp={event => {
        if (event.pointerType === 'touch' || event.pointerType === 'pen') setTouchPaused(paused => !paused);
      }}
    >
      <div className="app-promo-track" style={{ '--app-promo-duration': duration ?? '0s', '--app-promo-distance': `${groupWidth}px` } as React.CSSProperties}>
        <div ref={groupRef}><ContentGroup settings={settings} /></div>
        <div aria-hidden="true"><ContentGroup settings={settings} /></div>
      </div>
    </div>
    <AppIdentity app="doPuja" onOpenDownloadPanel={onOpenDownloadPanel} dialogId={dialogId} open={downloadOpen} />
    <div className="app-promo-action">
      <button type="button" aria-label="Get the apps" aria-haspopup="dialog" aria-expanded={downloadOpen} aria-controls={downloadOpen ? dialogId : undefined} className="app-promo-cta" onClick={onOpenDownloadPanel}>
        Get the apps <span aria-hidden="true">→</span>
      </button>
    </div>
    </section>
    <AppDownloadDialog settings={settings} id={dialogId} returnFocusRef={downloadTriggerRef} />
  </Dialog>;
}
